import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCurrentUserWorker } from "@/domains/workers/hooks/use-current-user-worker";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";

interface PermissionDebugCardProps {
  error: any;
  title?: string;
}

export function PermissionDebugCard({
  error,
  title = "Permission Error",
}: PermissionDebugCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuthStore();
  const { currentSalon, getSalonRole } = useSalonStore();
  const { hasWorkerProfile, workerProfile } = useCurrentUserWorker();

  const currentUserSalonRole = currentSalon ? getSalonRole(currentSalon.id) : null;

  const isPermissionError =
    error?.message?.includes("Cannot execute") || error?.message?.includes("ForbiddenError");

  if (!isPermissionError) {
    return null;
  }

  return (
    <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <CardTitle className="text-sm text-orange-800 dark:text-orange-200">{title}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-6 w-6 p-0"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="space-y-3 text-xs">
            <div className="bg-red-50 dark:bg-red-950/20 p-3 rounded border">
              <div className="font-medium text-red-800 dark:text-red-200 mb-1">Error Details:</div>
              <div className="text-red-700 dark:text-red-300 font-mono text-xs">
                {error?.message || "Unknown error"}
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded border">
              <div className="font-medium text-blue-800 dark:text-blue-200 mb-2">User Context:</div>
              <div className="space-y-1 text-blue-700 dark:text-blue-300">
                <div>
                  <strong>User ID:</strong> {user?.id || "None"}
                </div>
                <div>
                  <strong>Email:</strong> {user?.email || "None"}
                </div>
                <div>
                  <strong>User Global Roles:</strong> {JSON.stringify(user?.role) || "None"}
                </div>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded border">
              <div className="font-medium text-green-800 dark:text-green-200 mb-2">
                Salon Context:
              </div>
              <div className="space-y-1 text-green-700 dark:text-green-300">
                <div>
                  <strong>Salon:</strong> {currentSalon?.name || "None"} (
                  {currentSalon?.id || "None"})
                </div>
                <div>
                  <strong>Salon Role:</strong> {currentUserSalonRole || "None"}
                </div>
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-950/20 p-3 rounded border">
              <div className="font-medium text-purple-800 dark:text-purple-200 mb-2">
                Worker Status:
              </div>
              <div className="space-y-1 text-purple-700 dark:text-purple-300">
                <div>
                  <strong>Has Worker Profile:</strong> {hasWorkerProfile ? "Yes" : "No"}
                </div>
                <div>
                  <strong>Worker ID:</strong> {workerProfile?.id || "None"}
                </div>
                <div>
                  <strong>Worker Active:</strong> {workerProfile?.isActive ? "Yes" : "No"}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-950/20 p-3 rounded border">
              <div className="font-medium text-gray-800 dark:text-gray-200 mb-2">
                Suggested Solutions:
              </div>
              <div className="space-y-1 text-gray-700 dark:text-gray-300">
                <div>• Ensure you have a WORKER role in this salon</div>
                <div>• Check that your worker profile exists and is active</div>
                <div>• Contact your administrator to verify permissions</div>
                <div>• Try refreshing the page to reload your permissions</div>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
